import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './InfraestructuraCloudDevOpsPage.css';

const InfraestructuraCloudDevOpsPage = ({ darkMode, toggleDarkMode }) => {
  const beneficios = [
    {
      icon: '☁️',
      titulo: 'Escalabilidad Infinita',
      descripcion: 'Infraestructura que crece con tu negocio sin límites. Escala recursos automáticamente según la demanda.'
    },
    {
      icon: '⚡',
      titulo: 'Alta Disponibilidad',
      descripcion: 'Sistemas con 99.9% de uptime garantizado. Redundancia y backups automáticos para máxima disponibilidad.'
    },
    {
      icon: '💰',
      titulo: 'Reducción de Costos',
      descripcion: 'Paga solo por lo que usas. Elimina costos de hardware, mantenimiento y reduce gastos operativos.'
    },
    {
      icon: '🔒',
      titulo: 'Seguridad Avanzada',
      descripcion: 'Infraestructura segura con firewalls, encriptación, backups y cumplimiento normativo.'
    },
    {
      icon: '🚀',
      titulo: 'Despliegue Rápido',
      descripcion: 'CI/CD automatizado que permite desplegar cambios en minutos, no días.'
    },
    {
      icon: '📊',
      titulo: 'Monitoreo Continuo',
      descripcion: 'Monitoreo 24/7 de sistemas, alertas automáticas y dashboards en tiempo real.'
    }
  ];

  const servicios = [
    {
      titulo: 'Migración a Cloud',
      descripcion: 'Migración segura de tus sistemas a la nube: AWS, Azure, Google Cloud. Planificación, ejecución y optimización.',
      casos: [
        'Análisis de infraestructura actual',
        'Plan de migración personalizado',
        'Migración sin downtime',
        'Optimización post-migración'
      ]
    },
    {
      titulo: 'Infraestructura como Código (IaC)',
      descripcion: 'Infraestructura gestionada mediante código para versionado, reproducibilidad y automatización.',
      casos: [
        'Terraform, Ansible, CloudFormation',
        'Infraestructura versionada',
        'Despliegues reproducibles',
        'Gestión centralizada'
      ]
    },
    {
      titulo: 'Contenedores y Kubernetes',
      descripcion: 'Containerización de aplicaciones con Docker y orquestación con Kubernetes para escalabilidad y portabilidad.',
      casos: [
        'Containerización con Docker',
        'Orquestación con Kubernetes',
        'Auto-scaling',
        'Gestión de contenedores'
      ]
    },
    {
      titulo: 'CI/CD y Automatización',
      descripcion: 'Pipelines de integración y despliegue continuo para automatizar pruebas, builds y deployments.',
      casos: [
        'Pipelines CI/CD',
        'Automatización de tests',
        'Despliegues automáticos',
        'GitHub Actions, GitLab CI, Jenkins'
      ]
    },
    {
      titulo: 'Backups y Disaster Recovery',
      descripcion: 'Estrategias de backup automatizadas y planes de recuperación ante desastres para garantizar continuidad.',
      casos: [
        'Backups automatizados',
        'Backups en la nube',
        'Planes de recuperación',
        'Pruebas de restauración'
      ]
    },
    {
      titulo: 'Monitoreo y Observabilidad',
      descripcion: 'Monitoreo completo de infraestructura, aplicaciones y logs para detectar problemas antes de que afecten.',
      casos: [
        'Monitoreo 24/7',
        'Alertas automáticas',
        'Dashboards en tiempo real',
        'Análisis de logs'
      ]
    },
    {
      titulo: 'Gestión de Servidores',
      descripcion: 'Administración de servidores cloud y on-premise: configuración, mantenimiento y optimización.',
      casos: [
        'Gestión de servidores cloud',
        'Configuración y mantenimiento',
        'Optimización de rendimiento',
        'Gestión de actualizaciones'
      ]
    },
    {
      titulo: 'Networking y Seguridad',
      descripcion: 'Configuración de redes, VPNs, firewalls y seguridad de infraestructura cloud.',
      casos: [
        'Configuración de redes',
        'VPNs y conectividad segura',
        'Firewalls y seguridad',
        'Gestión de accesos'
      ]
    }
  ];

  const tecnologias = [
    {
      categoria: 'Cloud Providers',
      tecnologias: ['AWS', 'Microsoft Azure', 'Google Cloud', 'DigitalOcean']
    },
    {
      categoria: 'Contenedores',
      tecnologias: ['Docker', 'Kubernetes', 'Docker Compose', 'Helm']
    },
    {
      categoria: 'CI/CD',
      tecnologias: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'CircleCI']
    },
    {
      categoria: 'IaC',
      tecnologias: ['Terraform', 'Ansible', 'CloudFormation', 'Pulumi']
    },
    {
      categoria: 'Monitoreo',
      tecnologias: ['Prometheus', 'Grafana', 'Datadog', 'New Relic']
    }
  ];

  const proceso = [
    {
      paso: '1',
      titulo: 'Análisis de Infraestructura',
      descripcion: 'Evaluamos tu infraestructura actual, necesidades y objetivos para diseñar la mejor solución cloud.'
    },
    {
      paso: '2',
      titulo: 'Diseño de Arquitectura',
      descripcion: 'Diseñamos la arquitectura cloud: servidores, redes, seguridad, backups y alta disponibilidad.'
    },
    {
      paso: '3',
      titulo: 'Implementación',
      descripcion: 'Implementamos la infraestructura usando IaC, configuramos CI/CD y desplegamos aplicaciones.'
    },
    {
      paso: '4',
      titulo: 'Migración y Despliegue',
      descripcion: 'Migramos sistemas existentes a la nube y desplegamos nuevas aplicaciones de forma segura.'
    },
    {
      paso: '5',
      titulo: 'Monitoreo y Optimización',
      descripcion: 'Configuramos monitoreo, establecemos alertas y optimizamos continuamente la infraestructura.'
    }
  ];

  const sectores = [
    {
      titulo: 'E-commerce',
      descripcion: 'Infraestructura escalable para tiendas online con alta disponibilidad y auto-scaling según tráfico.'
    },
    {
      titulo: 'SaaS',
      descripcion: 'Infraestructura cloud para aplicaciones SaaS con multi-tenancy, escalabilidad y alta disponibilidad.'
    },
    {
      titulo: 'Startups',
      descripcion: 'Infraestructura cloud optimizada para startups: costos controlados, escalabilidad y despliegue rápido.'
    },
    {
      titulo: 'Empresas Establecidas',
      descripcion: 'Migración a cloud y modernización de infraestructura para empresas con sistemas legacy.'
    }
  ];

  return (
    <>
      <SEO 
        title="Infraestructura cloud y DevOps | AWS, Azure y Kubernetes | E-ASY"
        description="Migración a cloud, Kubernetes, CI/CD y monitorización para tener una infraestructura que se pueda operar. Proyectos en remoto para toda España, desde la sede en Tenerife."
        keywords="infraestructura cloud, DevOps, migración cloud, AWS, Azure, Google Cloud, Kubernetes, Docker, CI/CD, infraestructura como código, Terraform, cloud Canarias, DevOps Tenerife, infraestructura cloud España"
        canonical="https://e-asy.es/servicios/infraestructura-cloud-devops"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`infraestructura-cloud-devops-page ${darkMode ? 'dark' : 'light'}`}>
        
        {/* Hero Section */}
        <section className="infraestructura-hero">
          <div className="infraestructura-hero-content">
            <div className="infraestructura-hero-text">
              <h1 className="infraestructura-hero-title">Infraestructura Cloud y DevOps</h1>
              <p className="infraestructura-hero-subtitle">
                E-ASY ofrece servicios de <strong>infraestructura cloud</strong> y <strong>DevOps</strong> para empresas 
                que necesitan infraestructura escalable, alta disponibilidad y despliegues automatizados. 
                <strong>Migración a cloud</strong>, <strong>Kubernetes</strong>, <strong>CI/CD</strong> e 
                <strong>infraestructura como código</strong>.
              </p>
              <p className="infraestructura-hero-subtitle">
                Trabajamos con <strong>AWS</strong>, <strong>Azure</strong>, <strong>Google Cloud</strong> y más. 
                Desde <strong>Canarias</strong> ofrecemos servicios de infraestructura cloud para empresas en toda 
                <strong>España</strong> y proyectos <strong>internacionales</strong>. Si lo que buscáis es
                un equipo tecnológico externo para la pyme (no Kubernetes), la
                infra de proyectos está en{' '}
                <Link to="/servicios/azure">consultoría Azure</Link>
                {' '}y el paraguas en{' '}
                <Link to="/servicios/consultoria-microsoft-azure">
                  Microsoft / Azure / Workspace
                </Link>
                .
              </p>
              <div className="infraestructura-hero-cta">
                <Link to="/contacto" className="infraestructura-hero-button primary">
                  Solicita una consultoría gratuita
                </Link>
                <Link to="/servicios" className="infraestructura-hero-button secondary">
                  Ver todos los servicios
                </Link>
              </div>
            </div>
            <div className="infraestructura-hero-visual">
              <div className="infraestructura-hero-graphic">
                <div className="cloud-animation">
                  <div className="terminal-window">
                    <div className="terminal-header">
                      <div className="terminal-dots">
                        <span className="dot dot-red"></span>
                        <span className="dot dot-yellow"></span>
                        <span className="dot dot-green"></span>
                      </div>
                      <div className="terminal-title">Cloud Infrastructure</div>
                    </div>
                    <div className="terminal-body">
                      <div className="terminal-line">
                        <span className="terminal-prompt">$</span>
                        <span className="terminal-command">terraform apply</span>
                      </div>
                      <div className="terminal-line">
                        <span className="terminal-output">✓ Infrastructure deployed</span>
                      </div>
                      <div className="terminal-line">
                        <span className="terminal-prompt">$</span>
                        <span className="terminal-command">kubectl get pods</span>
                      </div>
                      <div className="terminal-line">
                        <span className="terminal-output">✓ All pods running</span>
                      </div>
                      <div className="terminal-line terminal-cursor">
                        <span className="terminal-prompt">$</span>
                        <span className="terminal-cursor-blink">_</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Qué es Section */}
        <section className="infraestructura-que-es">
          <div className="infraestructura-que-es-content">
            <div className="infraestructura-que-es-header">
              <h2 className="infraestructura-que-es-title">¿Qué es Infraestructura Cloud y DevOps?</h2>
              <p className="infraestructura-que-es-subtitle">
                La <strong>infraestructura cloud</strong> es la provisión de recursos informáticos (servidores, almacenamiento, 
                redes) a través de internet, eliminando la necesidad de hardware físico. <strong>DevOps</strong> es la práctica 
                de unificar desarrollo y operaciones para acelerar el despliegue de software mediante automatización.
              </p>
              <p className="infraestructura-que-es-subtitle">
                Si tu empresa necesita <strong>escalabilidad</strong>, <strong>alta disponibilidad</strong>, 
                <strong>despliegues rápidos</strong> o <strong>reducir costos de infraestructura</strong>, la combinación de 
                cloud y DevOps es la solución. Permite escalar recursos según demanda, desplegar cambios en minutos y 
                mantener sistemas siempre disponibles.
              </p>
            </div>
          </div>
        </section>

        {/* Beneficios Section */}
        <section className="infraestructura-beneficios">
          <div className="infraestructura-beneficios-content">
            <div className="infraestructura-beneficios-header">
              <h2 className="infraestructura-beneficios-title">Beneficios de Cloud y DevOps</h2>
              <p className="infraestructura-beneficios-subtitle">
                Ventajas de migrar a cloud y adoptar prácticas DevOps
              </p>
            </div>
            <div className="infraestructura-beneficios-grid">
              {beneficios.map((beneficio, index) => (
                <div key={index} className="infraestructura-beneficio-card">
                  <div className="beneficio-icon">{beneficio.icon}</div>
                  <h3 className="beneficio-title">{beneficio.titulo}</h3>
                  <p className="beneficio-description">{beneficio.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Servicios Section */}
        <section className="infraestructura-servicios">
          <div className="infraestructura-servicios-content">
            <div className="infraestructura-servicios-header">
              <h2 className="infraestructura-servicios-title">Servicios de Infraestructura Cloud y DevOps</h2>
              <p className="infraestructura-servicios-subtitle">
                Soluciones completas para modernizar tu infraestructura y acelerar despliegues
              </p>
            </div>
            <div className="infraestructura-servicios-grid">
              {servicios.map((servicio, index) => (
                <div key={index} className="infraestructura-servicio-card">
                  <h3 className="servicio-title">{servicio.titulo}</h3>
                  <p className="servicio-description">{servicio.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {servicio.casos.map((caso, idx) => (
                        <li key={idx} className="caso-item">{caso}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tecnologías Section */}
        <section className="infraestructura-tecnologias">
          <div className="infraestructura-tecnologias-content">
            <div className="infraestructura-tecnologias-header">
              <h2 className="infraestructura-tecnologias-title">Tecnologías que Utilizamos</h2>
              <p className="infraestructura-tecnologias-subtitle">
                Stack tecnológico moderno para infraestructura cloud y DevOps
              </p>
            </div>
            <div className="infraestructura-tecnologias-grid">
              {tecnologias.map((categoria, index) => (
                <div key={index} className="tecnologia-categoria-card">
                  <h3 className="categoria-title">{categoria.categoria}</h3>
                  <div className="tecnologias-tags">
                    {categoria.tecnologias.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proceso Section */}
        <section className="infraestructura-proceso">
          <div className="infraestructura-proceso-content">
            <div className="infraestructura-proceso-header">
              <h2 className="infraestructura-proceso-title">Nuestro Proceso</h2>
              <p className="infraestructura-proceso-subtitle">
                Metodología estructurada para implementar infraestructura cloud y DevOps
              </p>
            </div>
            <div className="infraestructura-proceso-timeline">
              {proceso.map((item, index) => (
                <div key={index} className="proceso-step">
                  <div className="step-number">{item.paso}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.titulo}</h3>
                    <p className="step-description">{item.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sectores Section */}
        <section className="infraestructura-sectores">
          <div className="infraestructura-sectores-content">
            <div className="infraestructura-sectores-header">
              <h2 className="infraestructura-sectores-title">Cloud y DevOps para Diferentes Sectores</h2>
              <p className="infraestructura-sectores-subtitle">
                Adaptamos la infraestructura cloud a las necesidades específicas de cada sector
              </p>
            </div>
            <div className="infraestructura-sectores-grid">
              {sectores.map((sector, index) => (
                <div key={index} className="infraestructura-sector-card">
                  <h3 className="sector-title">{sector.titulo}</h3>
                  <p className="sector-description">{sector.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="infraestructura-cta">
          <div className="infraestructura-cta-content">
            <h2 className="infraestructura-cta-title">¿Necesitas Infraestructura Cloud y DevOps?</h2>
            <p className="infraestructura-cta-subtitle">
              Agenda una consultoría gratuita y descubre cómo podemos modernizar tu infraestructura
            </p>
            <div className="infraestructura-cta-buttons">
              <Link to="/contacto" className="infraestructura-cta-button primary">
                Solicita una consultoría gratuita
              </Link>
              <Link to="/servicios" className="infraestructura-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>

      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default InfraestructuraCloudDevOpsPage;

